import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faEllipsisH, faFileLines } from '@fortawesome/free-solid-svg-icons';
import Table from '../../components/Listing/Table';
import Button from '../../components/Form/Button';
import FabGroup from '../../components/FabGroup';
import HelpArticleModal from '../../components/Modal/HelpArticleModal';
import { usePageTitle } from '../../layouts/PageTitleContext';
import { API_BASE_URL } from '../../api';

export default function BackofficeHelpArticles() {
    const { categoryId } = useParams();
    const { setTitle, setTitleIcon, setTitleDescription } = usePageTitle();
    const [category, setCategory] = useState(null);
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [keyword, setKeyword] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editArticle, setEditArticle] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        return Promise.all([
            axios.get(`${API_BASE_URL}/api/v1/backoffice/help/categories`, { withCredentials: true })
                .then(({ data }) => setCategory(data.find(c => String(c.id) === categoryId) ?? null)),
            axios.get(`${API_BASE_URL}/api/v1/backoffice/help/articles`, { withCredentials: true, params: { categoryId } })
                .then(({ data }) => setArticles(data)),
        ])
            .catch(() => toast.error('Impossible de charger les articles.'))
            .finally(() => setLoading(false));
    }, [categoryId]);

    useEffect(() => {
        document.title = 'Backoffice — Centre d\'aide';
        load();
    }, [load]);

    useEffect(() => {
        setTitle(category?.label ?? 'Articles');
        setTitleDescription("Articles du centre d'aide dans cette catégorie");
        setTitleIcon('ti-file-text');
        return () => { setTitleDescription(null); setTitleIcon(null); };
    }, [category, setTitle, setTitleDescription, setTitleIcon]);

    const openCreate = () => { setEditArticle(null); setShowModal(true); };
    const openEdit = (article) => { setEditArticle(article); setShowModal(true); };

    const handleDelete = (article) => {
        axios.delete(`${API_BASE_URL}/api/v1/backoffice/help/articles/${article.id}`, { withCredentials: true })
            .then(() => {
                toast.success('Article supprimé.');
                load();
            })
            .catch(() => toast.error('Une erreur est survenue.'));
    };

    const columns = [
        {
            accessor: 'imageUrl',
            Header: '',
            maxWidth: '60px',
            Cell: row => (
                <div style={{
                    width: 40, height: 40, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
                    background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {row.imageUrl ? (
                        <img src={row.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        <FontAwesomeIcon icon={faFileLines} style={{ color: '#9ca3af', fontSize: 14 }} />
                    )}
                </div>
            ),
        },
        {
            accessor: 'title',
            Header: 'Titre',
            sortable: true,
            minWidth: 220,
            Cell: row => <span style={{ fontWeight: 600 }}>{row.title}</span>,
        },
        {
            accessor: 'excerpt',
            Header: 'Extrait',
            Cell: row => <span style={{ fontSize: 13, color: '#6b7280' }}>{row.excerpt || '—'}</span>,
        },
        {
            accessor: 'published',
            Header: 'Statut',
            className: 'text-center',
            sortable: true,
            Cell: row => (
                <span style={{
                    fontSize: 11, fontWeight: 600,
                    background: row.published ? '#dcfce7' : '#f3f4f6',
                    color: row.published ? '#15803d' : '#6b7280',
                    borderRadius: 20, padding: '3px 10px',
                }}>
                    {row.published ? 'Publié' : 'Brouillon'}
                </span>
            ),
        },
        {
            accessor: 'actions',
            Header: '',
            className: 'col-actions',
            Cell: row => (
                <div className="cell-dropdown">
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                            <button className="dropdown-item" onClick={() => openEdit(row)}>Modifier</button>
                        </li>
                        <li>
                            <Button
                                className="dropdown-item text-danger"
                                onConfirm={() => handleDelete(row)}
                            >
                                Supprimer
                            </Button>
                        </li>
                    </ul>
                </div>
            ),
        },
    ];

    return (
        <>
            <Table
                columns={columns}
                data={articles}
                mode="client"
                loading={loading}
                keyword={keyword}
                setKeyword={setKeyword}
                searchPlaceholder="Rechercher un article..."
                rowId="id"
                pageSize={20}
                emptyMessage="Aucun article"
                onRowClick={openEdit}
            />

            <FabGroup buttons={[
                { id: 'new-article', icon: faCirclePlus, label: 'Nouvel article', onClick: openCreate },
            ]} />

            <HelpArticleModal
                show={showModal}
                onClose={() => setShowModal(false)}
                onSaved={load}
                categoryId={Number(categoryId)}
                article={editArticle}
            />
        </>
    );
}
